'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Search, Bell, Wallet, Menu, X, Zap, ChevronDown } from 'lucide-react';

interface AppTopbarProps {
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
}

export default function AppTopbar({ onToggleSidebar }: AppTopbarProps) {
  const [searchFocused, setSearchFocused] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const notifications = [
    { id: 'notif-1', text: 'You won ₹250 in Daily Quiz!', time: '2m ago', type: 'win' },
    { id: 'notif-2', text: 'Tournament starts in 15 minutes', time: '10m ago', type: 'alert' },
    { id: 'notif-3', text: 'Priya accepted your challenge', time: '1h ago', type: 'social' },
    { id: 'notif-4', text: 'Withdrawal of $45 approved', time: '3h ago', type: 'wallet' },
    { id: 'notif-5', text: 'New coding challenge available', time: '5h ago', type: 'activity' },
  ];

  return (
    <header className="flex items-center h-16 px-4 lg:px-6 gap-4 flex-shrink-0"
      style={{ background: 'rgba(13, 20, 36, 0.95)', borderBottom: '1px solid var(--border)', backdropFilter: 'blur(12px)' }}>
      {/* Mobile menu toggle */}
      <button
        onClick={onToggleSidebar}
        className="lg:hidden p-2 rounded-lg transition-colors hover:bg-muted"
        aria-label="Toggle menu"
      >
        <Menu size={20} style={{ color: 'var(--muted-foreground)' }} />
      </button>

      {/* Search */}
      <div className="flex-1 max-w-md relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
        <input
          type="text"
          placeholder="Search activities, quizzes, teams..."
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          className="w-full pl-9 pr-4 py-2 text-sm rounded-lg outline-none transition-all"
          style={{
            background: 'var(--muted)',
            border: `1px solid ${searchFocused ? 'var(--primary)' : 'var(--border)'}`,
            color: 'var(--foreground)',
          }}
        />
        {searchFocused && (
          <div className="absolute top-full mt-2 left-0 right-0 rounded-lg py-2 z-50"
            style={{ background: 'var(--card)', border: '1px solid var(--border)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
            <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>Trending</p>
            {['Daily Trivia Championship', 'Coding Hackathon', 'Speed Math Quiz'].map((s) => (
              <div key={`search-${s}`} className="px-3 py-2 text-sm hover:bg-muted cursor-pointer transition-colors" style={{ color: 'var(--foreground)' }}>
                {s}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 ml-auto">
        {/* XP bar */}
        <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
          <Zap size={14} style={{ color: '#F59E0B' }} />
          <span className="text-xs font-semibold" style={{ color: '#F59E0B' }}>2,840 XP</span>
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Lv.14</span>
        </div>

        {/* Wallet quick */}
        <Link href="/wallet-screen"
          className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors hover:bg-muted"
          style={{ background: 'rgba(124, 58, 237, 0.1)', border: '1px solid rgba(124, 58, 237, 0.3)' }}>
          <Wallet size={14} style={{ color: '#A78BFA' }} />
          <span className="text-xs font-semibold" style={{ color: '#A78BFA' }}>$124.50</span>
        </Link>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setNotifOpen(!notifOpen)}
            className="relative p-2 rounded-lg transition-colors hover:bg-muted"
            aria-label="Notifications"
          >
            <Bell size={18} style={{ color: 'var(--muted-foreground)' }} />
            <span className="absolute top-1 right-1 w-4 h-4 rounded-full text-xs flex items-center justify-center font-bold"
              style={{ background: '#F43F5E', color: '#fff', fontSize: '10px' }}>7</span>
          </button>
          {notifOpen && (
            <div className="absolute top-full right-0 mt-2 w-80 rounded-xl z-50 overflow-hidden"
              style={{ background: 'var(--card)', border: '1px solid var(--border)', boxShadow: '0 16px 48px rgba(0,0,0,0.5)' }}>
              <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
                <span className="text-sm font-semibold text-foreground">Notifications</span>
                <button onClick={() => setNotifOpen(false)}><X size={14} style={{ color: 'var(--muted-foreground)' }} /></button>
              </div>
              {notifications.map((n) => (
                <div key={n.id} className="px-4 py-3 border-b hover:bg-muted cursor-pointer transition-colors" style={{ borderColor: 'var(--border)' }}>
                  <p className="text-sm text-foreground">{n.text}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{n.time}</p>
                </div>
              ))}
              <div className="px-4 py-2 text-center">
                <span className="text-xs font-semibold" style={{ color: '#A78BFA', cursor: 'pointer' }}>View all notifications</span>
              </div>
            </div>
          )}
        </div>

        {/* Avatar */}
        <button className="flex items-center gap-2 p-1 rounded-lg transition-colors hover:bg-muted">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
            style={{ background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', color: '#fff' }}>
            AK
          </div>
          <ChevronDown size={14} style={{ color: 'var(--muted-foreground)' }} className="hidden md:block" />
        </button>
      </div>
    </header>
  );
}